import {
  AnnotateSVG,
  ArticleHeaderSVG,
  BookmarkHollowSVG,
  BooksSVG,
  CalendarDateSVG,
  ChecklistSVG,
  ClipboardCheckSVG,
  DetailsSVG,
  DistrictHollowSVG,
  FloppyHollowSVG,
  FolderHollowSVG,
  HeartHollowSVG,
  Icon,
  InsightsSVG,
  NewselaSVG,
  PaperHollowSVG,
  PowerWordHollowSVG,
  PreviewCardSVG,
  SchoolHollowSVG,
  ScienceSVG,
  ScrollOldSVG,
  SelectSVG,
  SpanishHollowSVG,
  StopSVG,
  SubjectElaSVG,
  SubjectEssentialsSVG,
  SubjectNewsSVG,
  SubjectSocialStudiesSVG,
  UserHollowSVG,
  VideoControlsSVG,
  VideoSVG,
  WireSVG,
} from "@newsela/angelou";

import { ALEXANDRIA_URL } from "@/alexandria/constants";
import ADMIN_CONSOLE_ROUTE_CONTENTS from "@/admin/constants/AdminConsoleRouteContents/AdminConsoleRouteContents";
import ADMIN_CONSOLE_ROUTES from "@/admin/constants/AdminConsoleRoutes/AdminConsoleRoutes";
import { inheritStrokeCss } from "@/base/NewselaGlobalLayoutV2/NavigationBar/shared/styles";
import {
  ADMIN_CONSOLE_CONTENT_PROMOTION_PATHNAME,
  PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE,
} from "@/components/ContentPromotions/constants";
import {
  CLASSROOM_DATA_REPORT,
  POWER_WORDS_REPORT,
} from "@/reports/teacher/constants";
import SubjectStemIcon from "@/icons/SubjectStem/RawSubjectStemIcon.svg.jsx";
import { UseGetUserPropertiesReturn } from "@/users/v3/useGetUserProperties";
import type { User } from "@/types/models/User/v3/User.interface";

export const SEARCH_SESSION_KEY = "searchSessionId";

export const NAVBAR_BREAKPOINTS = {
  xxl: 9999,
  lg: 1560,
  md: 1140,
  sm: 717,
  xs: 320,
};

export const COLLECTIONS_SLUGS = ["social-emotional-learning", "lgbtqia-ss"];

export enum ProductLicenseKeysEnum {
  ELA = "activeELALicense",
  SS = "activeSSLicense",
  SCI = "activeScienceLicense",
  STEM = "activeStemLicense",
}

export const ESSENTIALS_MENU_ITEM = {
  title: "Essentials",
  license: "activeEssentialsLicense",
  href: "/content/browse/news-browse-page/ckd4tly7100003dmt4gfi3fw6/",
  SvgComponent: SubjectEssentialsSVG,
  dataQASelector: "global-nav-essentials-product",
};

export const ELA_MENU_ITEM = {
  title: "ELA",
  license: ProductLicenseKeysEnum.ELA,
  href: "/content/browse/ela-browse-page/ckd51x1pr00023hoalzrknglr/",
  SvgComponent: SubjectElaSVG,
  dataQASelector: "global-nav-ela-product",
};

export const SS_MENU_ITEM = {
  title: "Social Studies",
  license: ProductLicenseKeysEnum.SS,
  href: "/content/browse/browse-social-studies/ckd0kn7y000013hqqp3k7t2yx/",
  SvgComponent: SubjectSocialStudiesSVG,
  dataQASelector: "global-nav-ss-product",
};

export const SCIENCE_MENU_ITEM = {
  title: "Science",
  license: ProductLicenseKeysEnum.SCI,
  href: "/content/browse/browse-science/ckd0jvn3z00023jn8mkcw1jjd/",
  SvgComponent: ScienceSVG,
  dataQASelector: "global-nav-science-product",
};

export const STEM_MENU_ITEM = {
  title: "STEM",
  license: ProductLicenseKeysEnum.STEM,
  href: "/content/subject/cmf4hlxr7000d3b6o1x3fxzvv",
  SvgComponent: SubjectStemIcon,
  dataQASelector: "global-nav-stem-product",
};

export const ASSIGNMENTS_MENU_ITEM_TEACHER = {
  title: "Assignments",
  href: "/binder/assignments",
  dataQASelector: "global-nav-assignments",
};

export const ASSIGNMENTS_MENU_ITEM_STUDENT = {
  title: "Assignments",
  href: "/assignments",
  dataQASelector: "global-nav-assignments",
};

export const SUBJECT_AREA_ITEMS = [
  ELA_MENU_ITEM,
  SS_MENU_ITEM,
  SCIENCE_MENU_ITEM,
  STEM_MENU_ITEM,
];

export const HOME_MENU_ITEMS = {
  ESSENTIALS: ESSENTIALS_MENU_ITEM,
  ELA: ELA_MENU_ITEM,
  SOCIAL_STUDIES: SS_MENU_ITEM,
  SCIENCE: SCIENCE_MENU_ITEM,
  ASSIGNMENTS: ASSIGNMENTS_MENU_ITEM_TEACHER,
};

export const MY_CONTENT_ITEMS = [
  {
    title: "Saved content",
    href: "/your-content/?saved-by-you=&content_type=allcontenttypes",
    SvgComponent: BookmarkHollowSVG,
    dataQASelector: "global-nav-content-library-saved-content",
    showForStudents: true,
  },
  {
    title: "Created by me",
    href: "/your-content/?created-by-you",
    SvgComponent: AnnotateSVG,
    dataQASelector: "global-nav-content-library-created-by-me",
    showForStudents: false,
  },
  {
    title: "Promoted at your district",
    href: PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE.district,
    SvgComponent: DistrictHollowSVG,
    dataQASelector: "global-nav-content-library-promoted-at-your-district",
    showForStudents: false,
  },
  {
    title: "Promoted at your school",
    href: PATHNAME_FOR_PROMOTED_AT_YOUR_VENUE.school,
    SvgComponent: DistrictHollowSVG,
    dataQASelector: "global-nav-content-library-promoted-at-your-school",
    showForStudents: false,
    badge: "New",
  },
  {
    title: "School text sets",
    href: "/your-content/?at-your-school",
    SvgComponent: FolderHollowSVG,
    dataQASelector: "global-nav-content-library-from-my-school",
    showForStudents: false,
  },
];

export const EXPLORE_CONTENT_ITEMS = [
  {
    title: "News",
    href: "/search/?article_type=ARTICLE_NEWS",
    SvgComponent: SubjectNewsSVG,
    dataQASelector: "global-nav-content-library-news",
    showForStudents: true,
    showForLite: true,
  },
  {
    title: "En Español",
    href: "/search/?languages=LANG_ES",
    SvgComponent: SpanishHollowSVG,
    dataQASelector: "global-nav-content-library-created-in-spanish",
    showForStudents: true,
    showForLite: true,
  },
  {
    title: "Articles and texts",
    href: "/search/?content_types=ARTICLE",
    SvgComponent: ArticleHeaderSVG,
    dataQASelector: "global-nav-content-library-articles-and-texts",
    showForStudents: true,
    showForLite: true,
  },
  {
    title: "Videos",
    href: "/search/?content_types=INTERACTIVE_VIDEO",
    SvgComponent: VideoSVG,
    dataQASelector: "global-nav-content-library-videos",
    showForStudents: true,
    showForLite: true,
  },
  {
    title: "Generation Genius",
    href: "/search/?content_types=GENERATION_GENIUS_VIDEO",
    SvgComponent: SubjectStemIcon,
    dataQASelector: "global-nav-content-library-generation-genius",
    showForStudents: true,
    showForLite: true,
    isGenerationGenius: true,
    badge: "New",
  },
  {
    title: "Text sets",
    href: "/search/?content_types=BUNDLE",
    SvgComponent: FolderHollowSVG,
    dataQASelector: "global-nav-content-library-text-sets",
    showForStudents: false,
    showForLite: true,
  },
  {
    title: "Standards",
    href: "/search/?viewAllStandards=true",
    SvgComponent: ChecklistSVG,
    dataQASelector: "global-nav-content-library-standards",
    showForStudents: false,
    showForLite: true,
  },
  {
    title: "Worksheets",
    href: "/search/?selectedWorksheet=true",
    SvgComponent: PaperHollowSVG,
    dataQASelector: "global-nav-content-library-worksheets",
    showForStudents: false,
    showForLite: true,
  },
  {
    title: "Content calendar",
    href: "https://newsela.com/resource-center/content-calendar?utm_source=newsela&utm_medium=product&utm_campaign=eng-calendar-june-2025&utm_term=all-customers",
    SvgComponent: CalendarDateSVG,
    dataQASelector: "global-nav-content-library-content-calendar",
    showForStudents: false,
    showForLite: false,
    openInNewTab: true,
  },
];

export const SUPPORT_CENTER_PROFESSIONAL_RESOURCES_ITEM = {
  title: "Professional learning resources",
  href: "/content/browse/educator-center-root",
  ItemIcon: <Icon SvgComponent={BooksSVG} />,
  openInNewTab: false,
  enabled: (_user: User, userProperties: Partial<UseGetUserPropertiesReturn>) =>
    !userProperties.isStudent,
};

export const SUPPORT_CENTER_ITEMS = [
  {
    title: "Get started with Newsela",
    href: "https://solutions.newsela.com/getting-started",
    ItemIcon: <Icon SvgComponent={NewselaSVG} __cssFor={inheritStrokeCss} />,
    openInNewTab: true,
    enabled: true,
  },
  SUPPORT_CENTER_PROFESSIONAL_RESOURCES_ITEM,
  {
    title: "Give feedback or share ideas",
    href: "https://newselamarketing.typeform.com/to/wikwNZul",
    ItemIcon: <Icon SvgComponent={HeartHollowSVG} />,
    openInNewTab: true,
    enabled: true,
  },
  {
    title: "Event calendar",
    href: "https://solutions.newsela.com/events",
    ItemIcon: <Icon SvgComponent={CalendarDateSVG} />,
    openInNewTab: true,
    enabled: (
      _user: User,
      userProperties: Partial<UseGetUserPropertiesReturn>,
    ) => !userProperties.isStudent && !userProperties.isParent,
  },
  {
    title: "Support center",
    href: "https://support.newsela.com/",
    ItemIcon: <Icon SvgComponent={PreviewCardSVG} />,
    openInNewTab: true,
    enabled: true,
  },
];

export const ADMIN_TOOLS_MENU_ADMIN_CONSOLE_FACULTY_ITEM = {
  title: "Admin console",
  href: "/admin/faculty",
  SvgComponent: UserHollowSVG,
  dataQASelector: "global-nav-admin-tools-console",
};

export const ADMIN_TOOLS_MENU_ADMIN_CONSOLE_DASHBOARD_ITEM = {
  title: ADMIN_CONSOLE_ROUTE_CONTENTS.ADMIN_CONSOLE,
  href: ADMIN_CONSOLE_ROUTES.ADMIN_CONSOLE,
  SvgComponent: UserHollowSVG,
  dataQASelector: "global-nav-admin-tools-dashboard",
};

export const ADMIN_TOOLS_MENU_IMPLEMENTATION_GUIDE_ITEM = {
  title: "Admin quick start",
  href: "/admin/implementation-guide",
  SvgComponent: ClipboardCheckSVG,
  dataQASelector: "global-nav-admin-tools-implementation-guide",
};

export const ADMIN_TOOLS_MENU_CONTENT_PROMOTION_ITEM = {
  title: "Content promotion",
  href: ADMIN_CONSOLE_CONTENT_PROMOTION_PATHNAME,
  SvgComponent: SchoolHollowSVG,
  dataQASelector: "global-nav-admin-tools-content-promotion",
};

export const ADMIN_TOOLS_MENU_CONTENT_RESTRICTION_ITEM = {
  title: "Content restriction",
  href: "/admin/content-restriction/",
  SvgComponent: StopSVG,
  dataQASelector: "global-nav-admin-tools-content-restriction",
};

export const STAFF_INTERNAL_TOOLS_ITEMS = [
  {
    title: "Chiron",
    href: "/chiron/",
    SvgComponent: DetailsSVG,
    dataQASelector: "global-nav-staff-tools-chiron",
  },
  {
    title: "Django",
    href: "/41fccbbef792859306e06585c6186718",
    SvgComponent: FloppyHollowSVG,
    dataQASelector: "global-nav-staff-tools-django",
  },
  {
    title: "Alexandria",
    href: ALEXANDRIA_URL,
    SvgComponent: BooksSVG,
    dataQASelector: "global-nav-staff-tools-alexandria",
  },
  {
    title: "Studio",
    href: "https://studio.newsela.com/home",
    SvgComponent: VideoControlsSVG,
    dataQASelector: "global-nav-staff-tools-studio",
  },
  {
    title: "The Wire",
    href: "/wire/board/",
    SvgComponent: WireSVG,
    dataQASelector: "global-nav-staff-tools-wire",
  },
  {
    title: "Metadata Manager",
    href: "/alexandria/metadata-manager",
    SvgComponent: SelectSVG,
    dataQASelector: "global-nav-staff-tools-metadata-manager",
  },
];

export const MY_PROGRESS_ITEM = {
  title: "My progress",
  href: "/student-binder/assignments",
  SvgComponent: InsightsSVG,
  dataQASelector: "global-nav-my-progress-my-progress",
};

export const MY_PROGRESS_WORD_WALL_ITEM = {
  title: "Word wall",
  href: "/student-binder/word-wall",
  SvgComponent: PowerWordHollowSVG,
  dataQASelector: "global-nav-my-progress-word-wall",
};

export const REPORTS_MENU_CLASSROOM_DATA_ITEM = {
  title: CLASSROOM_DATA_REPORT.name,
  href: CLASSROOM_DATA_REPORT.href,
  SvgComponent: InsightsSVG,
  dataQASelector: "global-nav-classes-reading-summary",
};

export const REPORTS_MENU_POWER_WORDS_ITEM = {
  title: POWER_WORDS_REPORT.name,
  href: `${POWER_WORDS_REPORT.href}?classroom_id=all`,
  SvgComponent: PowerWordHollowSVG,
  dataQASelector: "global-nav-classes-power-words",
};

export const REPORTS_MENU_NEW_POWER_WORDS_ITEM = {
  ...REPORTS_MENU_POWER_WORDS_ITEM,
  href: POWER_WORDS_REPORT.new_href,
};

export const REPORTS_MENU_USAGE_REPORT_ITEM = {
  title: "Usage report",
  href: "/reports/admin/usage-report",
  SvgComponent: ScrollOldSVG,
  dataQASelector: "global-nav-classes-usage-report",
};
